import React, {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { LocalStorageKey } from 'src/core/constants/storage';
import { setLocalItem } from 'src/core/utils/storage';

import styles from './Navigation.module.css';
import { useNavigationPinState } from './useNavigationPinState';

const NavigationContext = createContext<boolean | null>(null);

type Props = {
  children: ReactNode;
};

export const toggleForceToExpandNavigation = () => {
  if (document.documentElement.dataset.navExpanded === '1') {
    document.documentElement.dataset.navExpanded = '0';
    setLocalItem(LocalStorageKey.ExpandNavigation, '0', true);
  } else {
    document.documentElement.dataset.navExpanded = '1';
    setLocalItem(LocalStorageKey.ExpandNavigation, '1', true);
  }
};

export const NavigationProvider = ({ children }: Props) => {
  const navReference = useRef<HTMLElement>(null);
  const [isExpanded, setIsExpanded] = useNavigationPinState();

  useLayoutEffect(() => {
    if (isExpanded) {
      document.documentElement.dataset.navExpanded = '1';
    }
  }, []);

  useEffect(() => {
    const savedReference = navReference.current;
    if (savedReference) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentBoxSize) {
            const contentBoxSize = entry.contentBoxSize[0];
            setIsExpanded(contentBoxSize.inlineSize > 230);
          }
        }
      });
      resizeObserver.observe(savedReference);
      return () => resizeObserver.unobserve(savedReference);
    }
  }, [navReference]);

  return (
    <NavigationContext.Provider value={isExpanded}>
      <nav
        id="vertical"
        className={styles.nav}
        ref={navReference}
        aria-label="Main navigation"
      >
        {children}
      </nav>
    </NavigationContext.Provider>
  );
};

export function useNavigationContext() {
  const context = useContext(NavigationContext);
  if (context === null) {
    throw new Error(
      'useNavigationContext needs to be used within a NavigationContext.Provider',
    );
  }
  return context;
}
